/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {InvoiceTranslationApiService, InvoiceTranslationModel} from './invoice-translation-api.service';
import {InvoiceTranslationEditPageComponent} from './invoice-translation-edit-page.component';
import {InvoiceTranslationRoutePageComponent} from './invoice-translation-route-page.component';

export function invoiceTranslationStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(InvoiceTranslationApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageReportTemplates.apiKey);
  const rh = adnRouterHelper.createRouterHelper(
    'invoice-translations.invoice-translation',
    InvoiceTranslationApiService,
    'app.rep',
    'app'
  );

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {
        component: InvoiceTranslationRoutePageComponent,
        bindings: {readOnly: 'listReadOnly'}
      },
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());

  states.add(rh.itemPath(), rh.item({
    resolve: {
      invoiceTranslationPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: InvoiceTranslationModel = {
            id: $stateParams.id,
            objectState: 'ACTIVE',
            translations: {},
            validationWarnings: []
          };
          return new SignalAdminResourcePageState(model, true, readOnly(), api, 'Invoice Translation');
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Invoice Translation'));
      },
      model: function(invoiceTranslationPage: SignalAdminResourcePageState<InvoiceTranslationModel>) {
        return invoiceTranslationPage.model();
      }
    },
    views: {
      'content@app': {
        component: InvoiceTranslationEditPageComponent,
        bindings: {state: 'invoiceTranslationPage'}
      },
      'context@app': {
        component: SignalAdminResourceContextActionsComponent,
        bindings: {state: 'invoiceTranslationPage'}
      },
      'title@app': {
        component: SignalAdminResourceTitleComponent,
        bindings: {state: 'invoiceTranslationPage'}
      }
    }
  }));

  return states.build();
}
